import './styles.scss'

import React, { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { useActions, useValues, useMountedLogic } from 'kea'
import Modal from 'react-responsive-modal'

import railsContext from 'lib/selectors/rails-context'
import { useModalAwareIntersectionObserver } from 'lib/utils/use-modal-aware-intersection-observer'
import useModalHistory from 'lib/utils/use-modal-history'
import Helmet from 'lib/tags/helmet'
import Float from 'lib/tags/float'

import lightBoxLogic from '~/scenes/shared/profile/profile-header/lightbox/logic'

import ProfileHeader from './profile-header'
import Translate from './translate'
import TitlesSection from './titles-section'
import Locations from './locations'
import AboutTeacher from './about-teacher'
import GeneralInfo from './general-info'
import Skills from './skills'
import Reviews from './reviews'
import Booking from './booking'
import OtherCourses from './other-courses'
import ContactWidget from './contact-widget'
import Breadcrumbs from './breadcrumbs'
import SimilarCourses from './similar-courses'
import SharedModal from './shared-modal'
import PriceModalContent from './price-modal-content'
import SharingMenu from './sharing-menu'
import GoodFitModalContent from './good-fit-modal-content'
import BottomFloat from './bottom-float'

import sceneSaga from '~/scenes/course/show/saga'

import courseLogic from '~/scenes/course/show/logic'

export default function Show () {
  const { isDesktopWithInBetween } = useValues(courseLogic)

  const [isDesktopWithInBetweenBreakpoint, setIsDesktopWithInBetweenBreakpoint] = useState(false)

  useMountedLogic(sceneSaga)

  const { dpr, i18nLocale } = useSelector(railsContext)

  const {
    openModal,
    translateReview,
    loadMore,
    enableSharing,
    enablePriceModal,
    enableGoodFitModal,
    enableReviewModal,
    disableSharing,
    disablePriceModal,
    disableGoodFitModal,
    disableReviewModal
  } = useActions(courseLogic)
  const {
    course,
    userData,
    video,
    isOpenModal,
    helmet,
    translating,
    isLoading,
    avatar,
    isShareModalOpen,
    isPriceModalOpen,
    isGoodFitModalOpen,
    isReviewModalOpen
  } = useValues(courseLogic)

  const { isLightboxOpen } = useValues(lightBoxLogic({ user: userData }))

  const modalRef = useRef()

  const anyModalOpen =
    isOpenModal || isShareModalOpen || isPriceModalOpen || isGoodFitModalOpen || isReviewModalOpen || isLightboxOpen

  const [observerRef, isAboveClassTitleSectionVisible] = useModalAwareIntersectionObserver(
    { threshold: 0 },
    anyModalOpen
  )

  useEffect(() => {
    if (isOpenModal || isLightboxOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }

    return () => {
      document.body.style.overflow = ''
    }
  }, [isOpenModal, isLightboxOpen])

  useEffect(() => {
    setIsDesktopWithInBetweenBreakpoint(isDesktopWithInBetween)
  }, [isDesktopWithInBetween])

  useEffect(() => {
    const hideOrShowTawk = () => {
      if (typeof window !== 'undefined' && window.Tawk_API) {
        if (isLightboxOpen) {
          if (window.Tawk_API.hideWidget) {
            window.Tawk_API.hideWidget()
          }
        } else {
          if (window.Tawk_API.showWidget) {
            window.Tawk_API.showWidget()
          }
        }
      }
    }

    hideOrShowTawk()

    if (typeof window !== 'undefined') {
      if (!window.Tawk_API) {
        window.Tawk_API = {}
      }

      const originalOnLoad = window.Tawk_API.onLoad

      window.Tawk_API.onLoad = function () {
        if (originalOnLoad && typeof originalOnLoad === 'function') {
          originalOnLoad()
        }

        if (isLightboxOpen && window.Tawk_API && window.Tawk_API.hideWidget) {
          window.Tawk_API.hideWidget()
        }
      }
    }
  }, [isLightboxOpen])

  useModalHistory(isOpenModal, openModal, 'contact-teacher-modal')

  const {
    title,
    similarCourses,
    reputation,
    messageUrl,
    description,
    information,
    skills,
    otherCourses,
    teacherDescription,
    teacherEducation,
    teacherExperience
  } = course

  const teacherVideo = !video ? {} : video

  const otherCoursesExist = otherCourses && otherCourses.length !== 0
  const similarCoursesExist = similarCourses && similarCourses.length !== 0
  const courseReviewsExist = course && course.reviews && course.reviews.count !== 0

  const setIframeStyle = () => {
    const iframe = document.getElementById('message-modal-iframe')
    const iframeDocument = iframe.contentDocument || iframe.contentWindow.document

    if (iframeDocument) {
      iframeDocument.body.style.backgroundColor = 'transparent'

      const messageSceneElement = iframeDocument.querySelector('.message-scene')
      if (messageSceneElement) {
        messageSceneElement.classList.add('contact-teacher-iframe')
      }
    }
  }

  return (
    <>
      {helmet && <Helmet helmet={helmet} />}

      <div className='course-show-scene'>
        <div className='sections-container'>
          <div className='left-right-section'>
            <div id='left-section' className='left-section'>
              <ProfileHeader
                user={userData}
                course={course}
                video={teacherVideo}
                avatar={avatar}
                enableSharing={enableSharing}
                reputation={reputation}
                enableGoodFitModal={enableGoodFitModal}
                observerRef={observerRef}
              />

              <Translate />

              <TitlesSection
                title={title}
                description={description}
                information={information}
                enablePriceModal={enablePriceModal}
              />

              <Locations />

              {teacherDescription && teacherEducation && teacherExperience && (
                <AboutTeacher
                  teacherDescription={teacherDescription}
                  teacherEducation={teacherEducation}
                  teacherExperience={teacherExperience}
                />
              )}

              <div className='general-info-section'>
                <GeneralInfo />
              </div>

              <Skills skills={skills} />

              {courseReviewsExist && (
                <Reviews
                  loadMore={enableReviewModal}
                  isLoading={isLoading}
                  reviews={course.reviews}
                  translateReview={translateReview}
                  translating={translating}
                />
              )}

              <Booking otherCoursesExist={otherCoursesExist} />

              {otherCoursesExist && <OtherCourses courses={otherCourses} />}

              {!isDesktopWithInBetweenBreakpoint && (
                <div className={`breadcrumb-container${similarCoursesExist ? ' mb-40' : ' pb-40 mb-0'}`}>
                  <Breadcrumbs />
                </div>
              )}

              {!isDesktopWithInBetweenBreakpoint && <SimilarCourses dpr={dpr} courses={similarCourses} />}
            </div>

            <Float
              otherId='left-section'
              customWindowWidth={950}
              fromHeight={isAboveClassTitleSectionVisible ? 0 : 24}
              isSticky
            >
              <div className='right-section'>
                <ContactWidget isAboveClassTitleSectionVisible={isAboveClassTitleSectionVisible} />
              </div>
            </Float>
          </div>
          <div className='main-separator' />
        </div>

        <div className='wide-section'>
          <div className={`breadcrumb-container${similarCoursesExist ? ' mb-40' : ' pb-40 mb-0'}`}>
            <Breadcrumbs />
          </div>

          {similarCoursesExist && (
            <div className='similar-course-container-desktop'>
              <SimilarCourses dpr={dpr} i18nLocale={i18nLocale} courses={similarCourses} />
            </div>
          )}
        </div>

        <BottomFloat onClick={enableGoodFitModal} />

        {isShareModalOpen && (
          <SharedModal isOpen={isShareModalOpen} onClose={disableSharing} className='sharing-modal'>
            <SharingMenu />
          </SharedModal>
        )}

        {isPriceModalOpen && (
          <SharedModal isOpen={isPriceModalOpen} onClose={disablePriceModal} className='price-modal'>
            <PriceModalContent />
          </SharedModal>
        )}

        {isGoodFitModalOpen && (
          <SharedModal isOpen={isGoodFitModalOpen} onClose={disableGoodFitModal} className='goodfit-modal'>
            <GoodFitModalContent />
          </SharedModal>
        )}

        {isReviewModalOpen && (
          <SharedModal isOpen={isReviewModalOpen} onClose={disableReviewModal} className='review-modal'>
            <Reviews
              user={userData}
              loadMore={loadMore}
              isLoading={isLoading}
              reviews={course.reviews}
              translateReview={translateReview}
              translating={translating}
              isReviewModalOpen={isReviewModalOpen}
            />
          </SharedModal>
        )}

        <div className='popup-wrapper popup-content' ref={modalRef} />

        <Modal
          onOverlayClick={openModal}
          showCloseIcon={false}
          open={isOpenModal}
          onClose={() => {}}
          center
          blockScroll={false}
          classNames={{
            overlay: 'contact-teacher-overlay',
            modal: 'contact-teacher-modal'
          }}
          container={modalRef.current}
        >
          <iframe
            title='contact teacher modal'
            id='message-modal-iframe'
            src={`${messageUrl}&iframe=true`}
            width='640'
            height='709'
            className='contact-teacher-content'
            onLoad={setIframeStyle}
          />
        </Modal>
      </div>
    </>
  )
}
